import { MemoryFileSystem } from '@playcanvas/splat-transform';

import { Events } from './events';
import { isFirebaseConfigured, slugify, uploadStudioAsset } from './firebase';
import { isStudioPublishConfigured, studioPublishFunctionUrl, studioPublishToken } from './firebase-config';
import { defaultPostEffectSettings, ExperienceSettings, serializePlyCompressed } from './splat-serialize';
import { Splat } from './splat';

export type StudioPublishResult = {
    sceneId: string;
    sourceUrl: string;      // compressed .ply — this is Studio's `SimamScene.sourceUrl`
    settingsUrl: string;
    viewerUrl: string;       // studio.simamdigital.com/viewer/?content=...&settings=...
    editorUrl: string;       // link back into this editor (?content=... re-import)
};

// Studio's public viewer origin. Its index.html reads `?content=` (splat data)
// and `?settings=` (defaults to './settings.json' if omitted — not usable here
// since our two files live on Firebase Storage, not colocated with the viewer).
const STUDIO_ORIGIN = 'https://studio.simamdigital.com';

const registerStudioPublishEvents = (events: Events) => {
    events.function('studio.configured', () => isFirebaseConfigured() && isStudioPublishConfigured());

    // publish the current scene to Studio: compressed .ply + settings.json,
    // uploaded to THIS project's Firebase Storage (sim-editor — the same
    // bucket the Share dialog uses), then registered with Studio via its
    // `publishScene` Cloud Function. Studio (studio.simamdigital.com) is a
    // SEPARATE Firebase project (sim-4d-viewer-app) whose Firestore/Storage
    // rules explicitly block direct client writes — its `publicScenes`
    // collection can only be written by a trusted server context, hence the
    // function call instead of writing a database directly from here.
    //
    // path/record use a STABLE id (slug of the title, no timestamp) so
    // re-publishing the same scene overwrites its files and record in place
    // — matching the "republish replaces" convention already used by the
    // Firebase Share dialog (see firebase.ts's uploadScene).
    events.function('studio.publish', async (opts?: { title?: string, projectId?: string }): Promise<StudioPublishResult> => {
        if (!isFirebaseConfigured()) {
            throw new Error('Publishing is not configured. Add your Firebase web config in src/firebase-config.ts.');
        }
        if (!isStudioPublishConfigured()) {
            throw new Error('Studio publishing is not configured. Set studioPublishFunctionUrl/studioPublishToken in src/firebase-config.ts.');
        }

        const title = opts?.title || (events.invoke('doc.name') as string) || 'Untitled Scene';
        const sceneId = slugify(title);
        const projectId = opts?.projectId ? slugify(opts.projectId) : 'simam-project';

        events.fire('progressStart', 'Publishing to Studio…');
        try {
            events.fire('progressUpdate', { text: 'Packaging splat data…', progress: 5 });

            const splats = (events.invoke('scene.splats') as Splat[]) ?? [];
            if (splats.length === 0) {
                throw new Error('The scene has no splats to publish.');
            }

            const memFs = new MemoryFileSystem();
            await serializePlyCompressed(splats, { maxSHBands: 3, minOpacity: 1 / 255, removeInvalid: true }, memFs);
            const plyBytes = memFs.results.get('output.compressed.ply');
            if (!plyBytes || plyBytes.length === 0) {
                throw new Error('Failed to package the splat data.');
            }

            // build the same ExperienceSettings shape as the editor's own
            // "Export → Viewer" pipeline (export-popup.ts), so Studio's viewer
            // (the real @playcanvas/supersplat-viewer) reads it identically.
            const pose = events.invoke('camera.getPose') as { position: { x: number, y: number, z: number }, target: { x: number, y: number, z: number }, fov: number };
            const cameras = (pose?.position && pose?.target) ? [{
                initial: {
                    position: [pose.position.x, pose.position.y, pose.position.z] as [number, number, number],
                    target: [pose.target.x, pose.target.y, pose.target.z] as [number, number, number],
                    fov: pose.fov ?? 60
                }
            }] : [];

            const experienceSettings: ExperienceSettings = {
                version: 2,
                tonemapping: 'none',
                highPrecisionRendering: false,
                background: { color: [0, 0, 0] },
                postEffectSettings: defaultPostEffectSettings,
                animTracks: [],
                cameras,
                annotations: (events.invoke('annotations.export') as ExperienceSettings['annotations']) ?? [],
                startMode: 'default'
            };

            events.fire('progressUpdate', { text: 'Uploading splat data…', progress: 20 });
            const sourceUrl = await uploadStudioAsset(plyBytes, sceneId, 'scene.compressed.ply', 'application/octet-stream', (loaded, total) => {
                events.fire('progressUpdate', { text: 'Uploading splat data…', progress: total ? 20 + 60 * loaded / total : 50 });
            });

            events.fire('progressUpdate', { text: 'Uploading scene settings…', progress: 85 });
            const settingsUrl = await uploadStudioAsset(JSON.stringify(experienceSettings), sceneId, 'settings.json', 'application/json');

            const viewerUrl = `${STUDIO_ORIGIN}/viewer/?content=${encodeURIComponent(sourceUrl)}&settings=${encodeURIComponent(settingsUrl)}&nositemanager=1`;
            const editorUrl = `${location.origin}/?content=${encodeURIComponent(sourceUrl)}&title=${encodeURIComponent(title)}`;

            events.fire('progressUpdate', { text: 'Registering scene…', progress: 95 });

            // hand off to Studio's Cloud Function to write the publicScenes/{id}
            // record on ITS project — field names match packages/scene-data's
            // SimamScene type in the studio repo.
            const response = await fetch(studioPublishFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-publish-token': studioPublishToken
                },
                body: JSON.stringify({
                    sceneId,
                    projectId,
                    versionId: 'v1',
                    title,
                    tags: [],
                    sourceType: 'compressed-ply',
                    sourceUrl,
                    metadataUrl: settingsUrl,
                    viewerUrl,
                    editorUrl
                })
            });

            if (!response.ok) {
                const detail = await response.text().catch(() => '');
                throw new Error(`Studio registration failed (${response.status}). ${detail}`.trim());
            }

            events.fire('progressUpdate', { text: 'Done', progress: 100 });

            return { sceneId, sourceUrl, settingsUrl, viewerUrl, editorUrl };
        } finally {
            events.fire('progressEnd');
        }
    });
};

export { registerStudioPublishEvents };
